// src/components/common/stickers/StickerBadgeBar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../config/supabase';

export default function StickerBadgeBar({ noteBlockId, max = 10 }) {
  const [items, setItems] = useState([]);
  const [openIdx, setOpenIdx] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // initial load
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!noteBlockId) return;
      const { data, error } = await supabase
        .from('student_stickers')
        .select('sticker_image_url_snapshot, sticker_label_snapshot')
        .eq('note_block_id', noteBlockId)
        .order('created_at', { ascending: true });
      if (!error && alive) {
        setItems((data || []).map(r => ({
          image_url: r.sticker_image_url_snapshot,
          label: r.sticker_label_snapshot || 'Sticker',
        })));
      }
    })();
    return () => { alive = false; };
  }, [noteBlockId]);

  // live add
  useEffect(() => {
    const onAdded = (e) => {
      const d = e?.detail || {};
      if (!d.noteBlockId || d.noteBlockId !== noteBlockId || !d.imageUrl) return;
      setItems(prev => [...prev, { image_url: d.imageUrl, label: d.label || 'Sticker' }]);
    };
    window.addEventListener('mms:sticker-added', onAdded);
    return () => window.removeEventListener('mms:sticker-added', onAdded);
  }, [noteBlockId]);

  const visible = useMemo(() => items.slice(-max), [items, max]);
  const overflowCount = Math.max(0, items.length - visible.length);

  return (
    <>
      <div className="flex items-center">
        {visible.map((s, i) => (
          <button
            key={`${i}-${s.image_url}`}
            onClick={() => setOpenIdx(items.length - visible.length + i)}
            title={s.label}
            className="relative -ml-2 first:ml-0 focus:outline-none"
          >
            <img
              src={s.image_url}
              alt={s.label}
              className="w-10 h-10 rounded-xl shadow-md transition-transform hover:scale-105"
              // no bg / no border so transparent PNGs sit cleanly
            />
          </button>
        ))}
        {overflowCount > 0 && (
          <button
            onClick={() => setShowAll(true)}
            className="ml-2 px-2 h-6 rounded-full bg-white/90 border border-black/10 text-xs font-semibold grid place-items-center shadow-sm hover:bg-white"
            title="Show all stickers"
          >
            +{overflowCount}
          </button>
        )}
      </div>

      {/* Single sticker viewer (frameless) */}
      {openIdx != null && (
        <FramelessModal onClose={() => setOpenIdx(null)}>
          <FramelessSticker sticker={items[openIdx]} />
        </FramelessModal>
      )}

      {/* Gallery (keeps a card by default so the grid reads clearly) */}
      {showAll && (
        <FramelessModal onClose={() => setShowAll(false)}>
          <div className="w-[min(92vw,920px)] max-h-[86vh] overflow-auto rounded-2xl bg-white shadow-2xl border border-slate-200 p-4">
            <div className="text-base font-semibold text-slate-700 mb-3">All Stickers</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {items.map((s, idx) => (
                <button
                  key={`${idx}-${s.image_url}`}
                  onClick={() => setOpenIdx(idx)}
                  className="bg-white rounded-xl border border-slate-200 p-2 hover:shadow transition"
                  title={s.label}
                >
                  <img
                    src={s.image_url}
                    alt={s.label}
                    className="w-full aspect-square object-contain rounded-lg"
                  />
                  <div className="mt-1 text-[11px] text-slate-500 truncate">{s.label}</div>
                </button>
              ))}
            </div>
          </div>
        </FramelessModal>
      )}
    </>
  );
}

/* ───────────────── helpers ───────────────── */

function FramelessModal({ children, onClose }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const body = (
    <div
      className="fixed inset-0 z-[10000] grid place-items-center"
      aria-modal="true"
      role="dialog"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[1px]" />
      <div
        className={`relative z-10 ${mounted ? 'mms-zoom-in' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 h-9 px-3 rounded-full bg-white/90 border border-slate-200 shadow hover:bg-white text-sm"
          aria-label="Close"
          title="Close"
        >
          Close
        </button>
        {children}
      </div>

      <style>{`
        @keyframes mms-zoom-kf {
          0%   { transform: scale(.88) rotate(0.0001deg); opacity: 0; }
          55%  { transform: scale(1.06) rotate(0.0001deg); opacity: 1; }
          100% { transform: scale(1)    rotate(0.0001deg); opacity: 1; }
        }
        .mms-zoom-in { animation: mms-zoom-kf 260ms cubic-bezier(.22,1,.36,1); }
        @media (prefers-reduced-motion: reduce) { .mms-zoom-in { animation: none; } }
      `}</style>
    </div>
  );

  return createPortal(body, document.body);
}

function FramelessSticker({ sticker }) {
  if (!sticker) return null;
  return (
    <div className="w-[min(92vw,840px)] max-w-[840px]">
      <img
        src={sticker.image_url}
        alt={sticker.label}
        className="max-w-full max-h-[82vh] object-contain drop-shadow-2xl mms-pop-when-open"
      />
      <div className="mt-2 text-center text-sm text-white/90 drop-shadow">
        {sticker.label}
      </div>

      <style>{`
        @keyframes mms-pop-open {
          0%   { transform: scale(.96) rotate(0deg); }
          40%  { transform: scale(1.08) rotate(2deg); }
          70%  { transform: scale(1.02) rotate(-1.5deg); }
          100% { transform: scale(1)    rotate(0deg); }
        }
        .mms-pop-when-open { animation: mms-pop-open 420ms cubic-bezier(.22,1,.36,1); }
        @media (prefers-reduced-motion: reduce) { .mms-pop-when-open { animation: none; } }
      `}</style>
    </div>
  );
}